if (!customElements.get('view-toggle')) {
  class ViewToggle extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'theme-product-grid-view';
      this.defaultView = 'grid';
      this.gridButton = null;
      this.listButton = null;
      this.initialized = false;
    }

    connectedCallback() {
      // Prevent double initialization
      if (this.initialized) return;
      this.initialized = true;

      // Find button elements
      this.gridButton = this.querySelector('[data-view="grid"]');
      this.listButton = this.querySelector('[data-view="list"]');

      if (!this.gridButton || !this.listButton) {
        this.initialized = false;
        return;
      }

      // Bind events
      this.handleGridClick = () => this.setView('grid');
      this.handleListClick = () => this.setView('list');

      this.gridButton.addEventListener('click', this.handleGridClick);
      this.listButton.addEventListener('click', this.handleListClick);

      // Initialize view from localStorage
      this.initializeView();

      // Observe grid changes for infinite scroll support
      this.observeGridChanges();

      // Add resize listener to auto-switch to grid on small screens
      this.handleResize = () => this.checkViewportAndSwitch();
      window.addEventListener('resize', this.handleResize);
      this.checkViewportAndSwitch();
    }

    disconnectedCallback() {
      // Clean up event listeners when element is removed
      if (this.gridButton) {
        this.gridButton.removeEventListener('click', this.handleGridClick);
      }
      if (this.listButton) {
        this.listButton.removeEventListener('click', this.handleListClick);
      }
      window.removeEventListener('resize', this.handleResize);
      this.initialized = false;
    }

    initializeView() {
      let savedView = this.defaultView;

      // Only read from localStorage if NOT in design mode
      if (!Shopify.designMode) {
        savedView = localStorage.getItem(this.storageKey) || this.defaultView;
      }

      this.setView(savedView, false);
    }

    setView(view, saveToStorage = true) {
      // Get fresh reference to product grid
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;

      // Save to localStorage (skip in design mode)
      if (saveToStorage && !Shopify.designMode) {
        localStorage.setItem(this.storageKey, view);
      }

      // Update grid classes
      if (view === 'list') {
        productGrid.classList.add('product-grid--list-view');
        productGrid.classList.remove('product-grid--grid-view');
      } else {
        productGrid.classList.add('product-grid--grid-view');
        productGrid.classList.remove('product-grid--list-view');
      }

      // Update button active states
      this.gridButton.classList.toggle('view-toggle__button--active', view === 'grid');
      this.listButton.classList.toggle('view-toggle__button--active', view === 'list');

      // Update aria-pressed for accessibility
      this.gridButton.setAttribute('aria-pressed', view === 'grid');
      this.listButton.setAttribute('aria-pressed', view === 'list');

      // Re-render cards with appropriate card_style
      this.updateCardStyles(view);
    }

    updateCardStyles(view) {
      // Get fresh reference to product grid
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;

      // Find all product cards
      const cards = productGrid.querySelectorAll('.card');

      cards.forEach((card) => {
        if (view === 'list') {
          card.classList.add('card--horizontal');
          // Store original style if not already stored
          if (!card.dataset.originalStyle) {
            if (card.classList.contains('card--standard')) {
              card.dataset.originalStyle = 'standard';
            } else if (card.classList.contains('card--card')) {
              card.dataset.originalStyle = 'card';
            } else {
              card.dataset.originalStyle = 'card';
            }
          }
          card.classList.remove('card--standard', 'card--card');
        } else {
          card.classList.remove('card--horizontal');
          // Restore original card style
          const originalStyle = card.dataset.originalStyle || 'card';
          card.classList.add(`card--${originalStyle}`);
        }
      });
    }

    observeGridChanges() {
      // Get fresh reference to product grid
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            // Check if new nodes contain product cards
            const hasNewCards = Array.from(mutation.addedNodes).some(
              (node) => node.nodeType === 1 && (node.classList?.contains('grid__item') || node.querySelector?.('.card'))
            );
            if (hasNewCards) {
              const currentView = !Shopify.designMode
                ? localStorage.getItem(this.storageKey) || this.defaultView
                : this.defaultView;
              this.updateCardStyles(currentView);
            }
          }
        });
      });

      observer.observe(productGrid, { childList: true });
    }

    checkViewportAndSwitch() {
      // Auto-switch to grid view on screens smaller than 990px
      if (window.innerWidth < 990) {
        const productGrid = document.getElementById('product-grid');
        if (productGrid && productGrid.classList.contains('product-grid--list-view')) {
          this.setView('grid', false);
        }
      }
    }
  }

  customElements.define('view-toggle', ViewToggle);
}
